import { useCallback, useState } from 'react';
import { Link } from '~/components/global';
import { useEventListener, useIsomorphicLayoutEffect } from 'usehooks-ts';
import { clsx } from 'clsx';
import { useLayout } from '~/layouts';
import { MouseEvent } from 'react';

const debounce = (fn: (...args: any[]) => void, delay: number) => {
  let timerId: any = null;
  return (...args: any[]) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => fn(...args), delay);
  };
};

interface DealNavigationProps {
  sections: any[];
  onChange?: (id: string) => void;
}

export function DealNavigation({ sections, onChange }: DealNavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const { setHideHeader } = useLayout();

  const highlightSection = useCallback(
    (id: string) => {
      if (onChange) onChange(id);
      if (id) {
        setCurrent(id);
        history.pushState(null, '', `#${id}`);
      } else {
        setCurrent('');
        history.pushState(
          null,
          '',
          window.location.pathname + window.location.search,
        );
      }
    },
    [onChange],
  );

  const highlightAppropriateArea = useCallback(() => {
    const scrollY = window.pageYOffset;
    const entry = sections.find((section) => {
      const elementMatches = section.elements.find((el: HTMLElement) => {
        const sectionHeight = el.offsetHeight;
        const sectionTop = el.offsetTop;

        return (
          scrollY + 200 > sectionTop &&
          scrollY < sectionTop + sectionHeight - 68
        );
      });

      return !!elementMatches;
    });

    highlightSection(entry?.id);
  }, [highlightSection, sections]);

  useIsomorphicLayoutEffect(() => {
    //highlightAppropriateArea();
  }, [sections, highlightAppropriateArea]);

  const onScroll = debounce(() => {
    setIsOpen(window.scrollY >= 80);
    setHideHeader(window.scrollY >= 200);

    highlightAppropriateArea();
  }, 10);

  const scrollTo = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    highlightSection(id);
    const el = document.getElementById(id);
    const navbarHeight = 68;

    if (el) {
      window.scrollTo({
        top: el?.offsetTop - navbarHeight,
      });
    }
  };

  useEventListener('scroll', onScroll);

  return (
    <div
      id="pdp-sections"
      className={clsx('fixed inset-x-0 z-9', { open: isOpen })}
    >
      <div className="w-full overflow-hidden border-2 bg-white shadow-pdp">
        <ul className="mx-auto flex max-w-screen-2xl gap-x-8 px-8">
          {sections.map((section) => (
            <li key={section.id}>
              <Link
                href={`#${section.id}`}
                onClick={(e) => scrollTo(e, section.id)}
                className={`${
                  section.id === current
                    ? 'border-b-4 border-blue-500 text-gray-900'
                    : 'text-gray-800'
                } block whitespace-nowrap py-4 font-header text-xl font-semibold`}
              >
                {section.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
